//@ts-check
import React, { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { columnTitles } from '../options'
import { CAMPAIGN_INTERNAL_STATUSES, CAMPAIGN_DRAFT_STEPS } from '../../../../../../shared/constants'
import Box from '@material-ui/core/Box'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Chip from '@material-ui/core/Chip'
import InputLabel from '@material-ui/core/InputLabel'
import moment from 'moment'
import { mapValues, pickBy } from 'lodash'
import { Check } from '@material-ui/icons'
import Divider from '@material-ui/core/Divider'
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker'
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider'
import MomentUtils from '@date-io/moment'
import { validMoment } from '../../../../../../Utils/Date'
/** @import { Campaign, Country, Cities } from '../utils' */

/**
 * Opmistically update local state and sync with url parameters
 * @param {string} key
 * @returns {[string | null, (value: string | null) => void]}
 */
export function useSearchParams(key) {
  const history = useHistory()
  const [state, setState] = useState(() => {
    const params = new URLSearchParams(history.location.search)
    return params.get(key)
  })

  useEffect(() => {
    const params = new URLSearchParams(history.location.search)
    params.set(key, state || '')
    history.push({ ...history.location, search: `?${params.toString()}` })
  }, [state])

  return [state, setState]
}

/**
 * @param {Cities} cities
 */
export function useCityFilter(cities) {
  const [params, setParams] = useSearchParams('city')
  const city = useMemo(() => format(params), [params])

  /**
   * @param {string | null | undefined} value
   */
  function format(value) {
    if (!value) return ''
    if (
      cities.edges.some(
        ({ node }) => node.name.toLowerCase() === value.toLowerCase(),
      )
    ) {
      return value
    }

    console.error(`invalid city value: ${value}`)
    return ''
  }

  /**
   * @param { string } [city]
   */
  function setCity(city) {
    setParams(format(city))
  }

  return { city, setCity }
}

/**
 * @typedef {{ start: moment.Moment, end: moment.Moment }} DateRange
 * @typedef { (range: Partial<DateRange>) => void  } SetDateRange
 */
export function useDateRangeFilter() {
  const [startDate, setStartDate] = useSearchParams('startDate')
  const [endDate, setEndDate] = useSearchParams('endDate')

  const start = useMemo(
    () => validMoment(moment(startDate), moment().subtract(3, 'month')),
    [startDate],
  )

  const end = useMemo(
    () => validMoment(moment(endDate), moment()),
    [endDate],
  )

  /** @type { SetDateRange } */
  function setDateRange(range) {
    setStartDate(range.start?.format('YYYY-MM-DD') ?? null)
    setEndDate(range.end?.format('YYYY-MM-DD') ?? null)
  }

  /** @type {DateRange} */
  const dateRange = { start, end }
  return { dateRange, setDateRange }
}

/**
 * @typedef {{ draft: Record<string, boolean>, submitted: Record<string, boolean> }} StatusOptions
 * @returns {{
 *   status: StatusOptions
 *   setStatus: (status: StatusOptions) => void
 * }}
 */
export function useStatus() {
  const [params, setParams] = useSearchParams('status')
  const sections = {
    draft: {
      [CAMPAIGN_DRAFT_STEPS.HOME]: false,
      [CAMPAIGN_DRAFT_STEPS.ENTRY]: false,
      [CAMPAIGN_DRAFT_STEPS.DESIGN]: false,
      [CAMPAIGN_DRAFT_STEPS.DETAILS]: false,
      [CAMPAIGN_DRAFT_STEPS.REJECTED]: false,
    },
    submitted: {
      [CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW]: true,
      [CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW]: true,
      [CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF]: true,
      [CAMPAIGN_INTERNAL_STATUSES.PAYMENT]: true,
      [CAMPAIGN_INTERNAL_STATUSES.ACTIVE]: true,
    },
  }

  const status = useMemo(() => {
    if (params) {
      const entries = new Set(params.split(','))
      const draft = mapValues(sections.draft, (_v, k) => entries.has(k))
      const submitted = mapValues(sections.submitted, (_v, k) => entries.has(k))
      return { draft, submitted }
    }
    return sections
  }, [params])

  /**
   * @param { StatusOptions } status
   */
  function setStatus(status) {
    const enabled = pickBy(
      { ...status.draft, ...status.submitted },
      enabled => enabled,
    )
    setParams(Object.keys(enabled).join(','))
  }

  return { status, setStatus }
}

/**
 * @param {{
 *   value: string,
 *   onChange: (x: string) => void,
 * }} props
 */
export function SearchBar(props) {
  return (
    <Box>
      <InputLabel>Search</InputLabel>
      <TextField
        placeholder="Search anything"
        value={props.value}
        onChange={x => props.onChange(x.target.value)}
      />
    </Box>
  )
}

/**
 * @param {{
 *   value: string,
 *   onChange: (x?: string) => void,
 *   cities: string[],
 * }} props
 */
export function CitySelect(props) {
  return (
    <Box>
      <InputLabel id="city-select-label">City</InputLabel>
      <Select
        labelId="city-select-label"
        value={props.value}
        onChange={x => {
          props.onChange(String(x.target.value))
        }}
        displayEmpty
      >
        <MenuItem value="">Select City</MenuItem>
        {props.cities.map(x => (
          <MenuItem key={x} value={x}>
            {x}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

/**
 * @param {{
 *   value: DateRange,
 *   onChange: SetDateRange,
 * }} props
 */
export function DatePicker(props) {
  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
      <Box style={{ display: "flex", gap: 16 }}>
        <Box>
          <InputLabel>Start Date</InputLabel>
          <KeyboardDatePicker
            autoOk
            disableFuture
            variant="inline"
            format="LL"
            value={props.value.start}
            maxDate={props.value.end}
            onChange={start =>
              props.onChange({
                start: start ?? undefined,
                end: props.value.end,
              })
            }
          />
        </Box>
        <Box>
          <InputLabel>End Date</InputLabel>
          <KeyboardDatePicker
            autoOk
            disableFuture
            variant="inline"
            format="LL"
            minDate={props.value.start}
            value={props.value.end}
            onChange={end =>
              props.onChange({
                start: props.value.start,
                end: end ?? undefined,
              })
            }
          />
        </Box>
      </Box>
    </MuiPickersUtilsProvider>
  )
}

/**
 * @param {{
 *  status: StatusOptions
 *  onChange: (x: StatusOptions) => void,
 * }} props
 */
export function FilterChips({ status, onChange }) {
  /**
   * @param {"draft" | "submitted"} section
   * @param {string} key
   * @param {boolean} value
   */
  function toggle(section, key, value) {
    onChange({ ...status, [section]: { ...status[section], [key]: !value } })
  }

  return (
    <Box style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 10 }}>
      {Object.entries(status.draft).map(([label, enabled]) => (
        <CustomChip
          key={label}
          label={label}
          value={enabled}
          onClick={() => toggle('draft', label, enabled)}
        />
      ))}
      <Divider flexItem orientation="vertical" style={{ marginLeft: 8, marginRight: 8 }} />
      {Object.entries(status.submitted).map(([label, enabled]) => (
        <CustomChip
          key={label}
          label={label}
          value={enabled}
          onClick={() => toggle('submitted', label, enabled)}
        />
      ))}
    </Box>
  )
}

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {boolean} props.value
 * @param {() => void} props.onClick
 */
function CustomChip({ label, value, onClick }) {
  return (
    <Chip
      label={columnTitles[label]}
      clickable
      style={{
        color: value ? '#FFFFFF' : '#646478',
        backgroundColor: value ? '#646478' : '#EBEBEB',
        borderRadius: 4,
      }}
      deleteIcon={<Check style={{ color: value ? '#FFFFFF' : '#646478' }} />}
      onClick={onClick}
      onDelete={onClick}
    />
  )
}
